import React from 'react';
import Link from 'next/link';

import Chip from '@ui/Chip';
import Tooltip from '@ui/Tooltip';

import { ProductDocument } from '@util/types/firestore/products';
import { MasterDocument } from '@util/firestore/master';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import { BrandDocument } from '@util/types/firestore/brand';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';
import { formatDateCT } from '@util/dateFormat';
import SpecificationsSection from '@c/master/SpecificationsSection';

type Props = {
  product: ProductDocument;
  breadcrumbs?: BreadcrumbItem[];
  master?: MasterDocument[];
  brand?: BrandDocument | null;
  conditionDescription?: string | null;
};

// Timezone-pinned (America/Chicago) so server and client render an identical
// string — no hydration mismatch near day boundaries, no layout shift. '—' is
// the placeholder when the date is missing/malformed.
const formatDate = (d?: number | null) => formatDateCT(d) || '—';

const ProductOverviewCard: React.FC<Props> = ({
  product,
  breadcrumbs = [],
  master = [],
  brand = null,
  conditionDescription = null,
}) => {
  const views =
    typeof product?.counter?.views === 'number' ? product.counter.views : 0;

  return (
    <div className="w-full">
      {/* Two-column definition list keeps labels aligned */}
      <dl className="grid grid-cols-[12rem_1fr] gap-x-12 gap-y-3">
        {/* Listed On */}
        <dt className="text-[1.5rem] text-gray-700">Listed On:</dt>
        <dd className="text-[1.4rem]">{formatDate(product?.created)}</dd>

        {/* Last Updated */}
        <dt className="text-[1.5rem] text-gray-700">Last Updated:</dt>
        <dd className="text-[1.4rem]">{formatDate(product?.date_updated)}</dd>

        {/* Condition */}
        <dt className="text-[1.5rem] text-gray-700">Condition:</dt>
        <dd className="text-[1.4rem]">
          <div className="flex flex-wrap items-center gap-2">
            {conditionDescription ? (
              <Tooltip content={conditionDescription}>
                <Chip
                  itemProp="itemCondition"
                  color="blue"
                  text={getConditionShortLabel(product?.condition) || '—'}
                  className="cursor-pointer"
                />
              </Tooltip>
            ) : (
              <Chip
                itemProp="itemCondition"
                color="blue"
                text={getConditionShortLabel(product?.condition) || '—'}
              />
            )}
          </div>
        </dd>

        {/* Views */}
        {views > 0 && (
          <>
            <dt className="text-[1.5rem] text-gray-700">Views:</dt>
            <dd className="text-[1.4rem]">{views}</dd>
          </>
        )}

        {/* Category */}
        <dt className="text-[1.5rem] text-gray-700">Category:</dt>
        <dd className="text-[1.4rem]">
          <div className="flex flex-wrap items-center gap-2">
            {breadcrumbs.slice(1, -1).map((b, i) => (
              <Chip
                key={`${b.href}-${i}`}
                href={b.href}
                text={b.label}
                color="primary"
              />
            ))}
            {master?.length > 0 &&
              master.map((m) => (
                <Chip
                  key={m.id}
                  href={`/m/${m.slug}`}
                  text={m.title}
                  color="primary"
                />
              ))}
          </div>
        </dd>

        {/* Brand */}
        <dt className="text-[1.5rem] text-gray-700">Brand:</dt>
        <dd className="text-[1.4rem]">
          {brand?.url ? (
            <Link
              href={`/brands/${brand.url}`}
              className="font-semibold text-brand-blue hover:underline"
            >
              {product?.brand || '—'}
            </Link>
          ) : (
            <span className="font-semibold">{product?.brand || '—'}</span>
          )}
        </dd>

        {/* SKU */}
        {product?.sku && (
          <>
            <dt className="text-[1.5rem] text-gray-700">Sku:</dt>
            <dd className="text-[1.4rem]">{product.sku}</dd>
          </>
        )}
      </dl>

      {/* GearMaster spec table — SSR'd fact data for GEO/AEO extraction.
          The data already ships in __NEXT_DATA__ via the master prop; this renders it
          into visible HTML. Self-guards to null when there are no specs, so listings
          without a GearMaster match render exactly as before (no orphan heading). */}
      {master.length > 0 && (
        <SpecificationsSection master={master[0]} heading="Specifications" />
      )}
    </div>
  );
};

export default ProductOverviewCard;
